import { cx } from '@linaria/core';
import { useRef, useState, useEffect } from 'react';
import { Slot } from '../comp.slot.js';
import { waitForContent } from '../util.wait-for-content.js';
import { templateContainer, tokens, pageSelectors, paddingTop, pageContent, paddingBottom, footer, page } from './_split-pages.style.js';
import { constructPhysicalPages } from './util.construct-physical-pages.js';
import { jsxs, jsx } from 'react/jsx-runtime';

/**
 * Constitutes a single SplitPrintPage, _however_ such a page may span multiple
 * physical pages depending on its content.
 */
function SplitPhysicalPages(props) {
  const ref = useRef(null);
  const templateSourceRef = useRef(null);
  const templateDestinationRef = useRef(null);
  const [amountOfPages, setAmountOfPages] = useState();
  useEffect(() => {
    const source = templateSourceRef.current;
    const destination = templateDestinationRef.current;
    if (!source || !destination) {
      return;
    }
    waitForContent(source).then(() => {
      constructPhysicalPages({
        target: props.target,
        source: source,
        destination: destination,
        lineCount: props.page.lineCount,
        padding: props.page.padding,
        currentContext: props.context,
        slots: props.config.slots
      }).then(amountOfPages => {
        setAmountOfPages(amountOfPages);
        props.onPhysicalPagesRendered(props.context.virtual.currentPageIndexInSection, amountOfPages);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/jsxs("div", {
    ref: ref,
    children: [!!amountOfPages && !!props.context.physical && new Array(amountOfPages).fill(undefined).map((it, index) => /*#__PURE__*/jsx(PhysicalPage, {
      ...props,
      preparedPagesContentRef: templateDestinationRef,
      physicalPageIndex: index
    }, index)), /*#__PURE__*/jsxs("div", {
      className: templateContainer,
      "data-template": true,
      children: [/*#__PURE__*/jsx("div", {
        ref: templateSourceRef,
        children: props.page.content
      }), /*#__PURE__*/jsx("div", {
        ref: templateDestinationRef
      })]
    })]
  });
}
function PhysicalPage(props) {
  const pageRef = useRef(null);
  const contentRef = useRef(null);
  const context = {
    format: props.context.format,
    virtual: props.context.virtual,
    physical: props.context.physical ? {
      ...props.context.physical,
      currentPageIndexInSection: props.context.physical.currentPageIndexInSection + props.physicalPageIndex,
      currentPageIndexInDocument: props.context.physical.currentPageIndexInDocument + props.physicalPageIndex
    } : undefined
  };
  const padding = props.page.padding?.(context);

  //
  // Inserts the prepared content from our template into the actual physical
  // page
  //
  useEffect(() => {
    const page = pageRef.current;
    const content = contentRef.current;
    const preparedPagesContent = props.preparedPagesContentRef.current;
    const templateForPage = preparedPagesContent?.children[props.physicalPageIndex]?.cloneNode(true);
    if (!content || !templateForPage || !page) {
      return;
    }
    content.replaceChildren(templateForPage);
  }, [props.preparedPagesContentRef, props.physicalPageIndex]);
  return /*#__PURE__*/jsxs("div", {
    ref: pageRef,
    className: cx(page, props.page.className),
    style: tokens.__override({
      paddingTop: padding?.top,
      paddingBottom: padding?.bottom
    }),
    "data-physical-page": true,
    children: [!!props.config.slots?.header && /*#__PURE__*/jsx("div", {
      className: pageSelectors.header.className,
      children: /*#__PURE__*/jsx(Slot, {
        slot: props.config.slots.header,
        context: context
      })
    }), /*#__PURE__*/jsx("div", {
      className: paddingTop
    }), /*#__PURE__*/jsx("div", {
      className: pageContent,
      ref: contentRef
    }), /*#__PURE__*/jsx("div", {
      className: paddingBottom
    }), !!props.config.slots?.footer && /*#__PURE__*/jsx("div", {
      className: footer,
      children: /*#__PURE__*/jsx(Slot, {
        context: context,
        slot: props.config.slots?.footer
      })
    })]
  });
}

export { SplitPhysicalPages };
//# sourceMappingURL=_split-pages.js.map
